import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { getHitLabApiMessage } from './hitLabUtils';

const INITIAL_DATA = {
  sourceFilterSupported: false,
  sourceFilterOptions: [],
  ctaArrivals: null,
  listeningEvidence: { metrics: {} },
  feedbackPasses: { metrics: {} },
  voiceDrops: { metrics: {} },
  catalogHealth: { metrics: {} },
  sourceMix: { sources: {} },
};

const WINDOW_OPTIONS = [1, 7, 30, 90];

const SOURCE_LABELS = {
  all: 'All sources',
  cl_creative_feedback: 'Craigslist creative feedback',
  hitlab_radio: 'HitLab Radio',
  direct: 'Direct',
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatRate = (value) => `${formatCount(value)}%`;

const formatJgk = (value) => `${formatCount(value)} JGK`;

const formatUsd = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const formatDuration = (value) => {
  const totalSeconds = Math.max(0, Math.floor(Number(value || 0)));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const getMetric = (group, key) => group?.metrics?.[key] || 0;

function MetricCard({ label, value, detail = '' }) {
  return (
    <article className="hitlab-radio-listener-funnel-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

function MetricGroup({ title, group, children }) {
  if (group?.available === false) {
    return (
      <section className="hitlab-radio-listener-funnel-group">
        <h3>{title}</h3>
        <div className="hitlab-alert">{group.reason || 'Metric group unavailable.'}</div>
      </section>
    );
  }

  return (
    <section className="hitlab-radio-listener-funnel-group">
      <h3>{title}</h3>
      <div className="hitlab-radio-listener-funnel-grid">{children}</div>
    </section>
  );
}

export default function HitLabRadioListenerFunnelMetrics({
  authConfig,
  refreshKey = 0,
}) {
  const [windowDays, setWindowDays] = useState('1');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadMetrics = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    setLoading(true);
    setError('');
    return axiosConn
      .get('hitlab/admin/radio/listener-funnel-metrics', {
        ...authConfig,
        params: {
          windowDays,
          source: sourceFilter,
        },
      })
      .then((response) => {
        setData(response.data?.data || INITIAL_DATA);
      })
      .catch((err) => {
        setError(
          getHitLabApiMessage(
            err,
            'JG Radio Listener Funnel could not be loaded.',
          ),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authConfig, sourceFilter, windowDays]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics, refreshKey]);

  const sourceOptions = useMemo(() => {
    const options = Array.isArray(data.sourceFilterOptions)
      ? data.sourceFilterOptions
      : [];
    return options.filter((option) => Object.prototype.hasOwnProperty.call(SOURCE_LABELS, option));
  }, [data.sourceFilterOptions]);

  const sourceFilterAvailable =
    data.sourceFilterSupported === true && sourceOptions.length > 0;
  const listeningEvidence = data.listeningEvidence || { metrics: {} };
  const ctaArrivals = data.ctaArrivals || { metrics: {} };
  const feedbackPasses = data.feedbackPasses || { metrics: {} };
  const voiceDrops = data.voiceDrops || { metrics: {} };
  const catalogHealth = data.catalogHealth || { metrics: {} };
  const sourceMix = data.sourceMix || { sources: {} };

  return (
    <section
      className="hitlab-radio-listener-funnel"
      aria-label="JG Radio Listener Funnel"
    >
      <div className="hitlab-radio-listener-funnel-header">
        <div>
          <p className="hitlab-kicker">RADIO LISTENER FUNNEL</p>
          <h2>JG Radio Listener Funnel</h2>
          <p>
            Craigslist listener test activity across playback, feedback passes,
            rewards, and listener drops.
          </p>
        </div>
        <div className="hitlab-radio-listener-funnel-actions">
          <label>
            Window
            <select
              value={windowDays}
              onChange={(event) => setWindowDays(event.target.value)}
            >
              {WINDOW_OPTIONS.map((days) => (
                <option key={days} value={String(days)}>
                  {days} day{days === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </label>
          {sourceFilterAvailable ? (
            <label>
              Source
              <select
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
              >
                {sourceOptions.map((source) => (
                  <option key={source} value={source}>
                    {SOURCE_LABELS[source]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className="hitlab-secondary-btn"
            disabled={loading}
            onClick={loadMetrics}
          >
            {loading ? 'Loading...' : 'Refresh funnel'}
          </button>
        </div>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {sourceFilter !== 'all' && listeningEvidence.reason ? (
        <div className="hitlab-radio-listener-funnel-note">
          Listener playback remains all-playback; source applies to reward-pass
          lineage.
        </div>
      ) : null}

      <MetricGroup title="Radio Arrival" group={ctaArrivals}>
        <MetricCard
          label="Radio Landed"
          value={formatCount(getMetric(ctaArrivals, 'landed'))}
          detail={
            ctaArrivals.sourceFilterApplied
              ? `${SOURCE_LABELS[sourceFilter] || 'Selected'} source`
              : 'Real /hitlab/radio page views'
          }
        />
      </MetricGroup>

      <MetricGroup title="Listener Activity" group={listeningEvidence}>
        <MetricCard label="Starts" value={formatCount(getMetric(listeningEvidence, 'starts'))} />
        <MetricCard
          label="Qualified Listens"
          value={formatCount(getMetric(listeningEvidence, 'qualifiedListens'))}
        />
        <MetricCard
          label="Known Listeners"
          value={formatCount(getMetric(listeningEvidence, 'knownListeners'))}
        />
        <MetricCard
          label="Anonymous Sessions"
          value={formatCount(getMetric(listeningEvidence, 'anonymousSessions'))}
        />
        <MetricCard
          label="Repeat Known"
          value={formatCount(getMetric(listeningEvidence, 'repeatKnownListeners'))}
        />
        <MetricCard
          label="Listen Time"
          value={formatDuration(getMetric(listeningEvidence, 'totalListenedSeconds'))}
        />
        <MetricCard
          label="Track Feedback"
          value={formatCount(getMetric(listeningEvidence, 'feedbackCount'))}
        />
        <MetricCard
          label="Support Gross"
          value={formatJgk(getMetric(listeningEvidence, 'supportJgkGross'))}
        />
      </MetricGroup>

      <MetricGroup title="Feedback Passes" group={feedbackPasses}>
        <MetricCard label="Started" value={formatCount(getMetric(feedbackPasses, 'started'))} />
        <MetricCard
          label="Submitted"
          value={formatCount(getMetric(feedbackPasses, 'submitted'))}
        />
        <MetricCard
          label="Pending Review"
          value={formatCount(getMetric(feedbackPasses, 'pendingReview'))}
        />
        <MetricCard label="Approved" value={formatCount(getMetric(feedbackPasses, 'approved'))} />
        <MetricCard label="Rejected" value={formatCount(getMetric(feedbackPasses, 'rejected'))} />
        <MetricCard
          label="Completion Rate"
          value={formatRate(getMetric(feedbackPasses, 'completionRate'))}
        />
        <MetricCard
          label="Approval Rate"
          value={formatRate(getMetric(feedbackPasses, 'approvalRate'))}
        />
        <MetricCard
          label="Reward Issued"
          value={formatCount(getMetric(feedbackPasses, 'rewardIssuedCount'))}
          detail={formatJgk(getMetric(feedbackPasses, 'rewardJgkIssued'))}
        />
        <MetricCard
          label="Reward Value"
          value={formatUsd(getMetric(feedbackPasses, 'rewardGrossCashoutValueUsd'))}
        />
      </MetricGroup>

      <MetricGroup title="Voice Drops" group={voiceDrops}>
        <MetricCard label="Submitted" value={formatCount(getMetric(voiceDrops, 'submitted'))} />
        <MetricCard label="Approved" value={formatCount(getMetric(voiceDrops, 'approved'))} />
        <MetricCard
          label="Live in Rotation"
          value={formatCount(getMetric(voiceDrops, 'liveInRotation'))}
        />
        <MetricCard label="Rejected" value={formatCount(getMetric(voiceDrops, 'rejected'))} />
        <MetricCard label="Disabled" value={formatCount(getMetric(voiceDrops, 'disabled'))} />
        <MetricCard
          label="Removed"
          value={formatCount(getMetric(voiceDrops, 'removedFromLiveRotation'))}
        />
        <MetricCard
          label="Approval Rate"
          value={formatRate(getMetric(voiceDrops, 'approvalRate'))}
        />
      </MetricGroup>

      <MetricGroup title="Catalog Health" group={catalogHealth}>
        <MetricCard
          label="Active Tracks"
          value={formatCount(getMetric(catalogHealth, 'activeTracks'))}
        />
        <MetricCard
          label="Active Listener Drops"
          value={formatCount(getMetric(catalogHealth, 'activeListenerDrops'))}
        />
        <MetricCard
          label="Active Drops"
          value={formatCount(getMetric(catalogHealth, 'activeDrops'))}
        />
        <MetricCard
          label="Active Bumpers"
          value={formatCount(getMetric(catalogHealth, 'activeBumpers'))}
        />
        <MetricCard
          label="Total Active"
          value={formatCount(getMetric(catalogHealth, 'totalActiveItems'))}
        />
      </MetricGroup>

      <section className="hitlab-radio-listener-funnel-group">
        <h3>Source Mix</h3>
        {sourceMix.available === false ? (
          <div className="hitlab-alert">{sourceMix.reason || 'Source mix unavailable.'}</div>
        ) : (
          <div className="hitlab-radio-listener-source-mix">
            {Object.entries(SOURCE_LABELS)
              .filter(([source]) => source !== 'all')
              .map(([source, label]) => {
                const sourceData = sourceMix.sources?.[source] || {};
                return (
                  <article key={source}>
                    <strong>{label}</strong>
                    <span>{formatCount(sourceData.started)} started</span>
                    <span>{formatCount(sourceData.submitted)} submitted</span>
                    <span>{formatCount(sourceData.approved)} approved</span>
                    <span>{formatCount(sourceData.rejected)} rejected</span>
                  </article>
                );
              })}
          </div>
        )}
      </section>
    </section>
  );
}
